const SITE_NAME = 'League Runner';
const SITE_URL = 'https://leaguerunner.net';
const LOGO_URL = `${SITE_URL}/leaguerunner.png`;
const DEFAULT_IMAGE_URL = `${SITE_URL}/image1.jpg?v=20260801`;
const DEFAULT_DESCRIPTION =
  'League Runner is a fantasy running league platform for drafting teams, tracking mileage, and competing across a full season.';

const ORGANIZATION_SCHEMA = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: LOGO_URL,
};

const PUBLIC_ROUTE_METADATA = {
  '/': {
    title: 'League Runner | Fantasy Running Leagues',
    description:
      'League Runner is the fantasy league where the players are the athletes. Create leagues, draft runners, track mileage, and compete all season.',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        ORGANIZATION_SCHEMA,
        {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_URL,
          description: DEFAULT_DESCRIPTION,
          publisher: ORGANIZATION_SCHEMA,
        },
      ],
    },
  },
  '/about': {
    title: 'About League Runner | Ethan Pelton',
    description:
      'Learn how Ethan Pelton built League Runner from a family passion for running, climbing, competition, and community.',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About League Runner',
      url: `${SITE_URL}/about`,
      mainEntity: {
        '@type': 'Person',
        name: 'Ethan Pelton',
        description:
          'Founder of League Runner and outdoor enthusiast focused on running, family, and community competition.',
      },
      publisher: {
        ...ORGANIZATION_SCHEMA,
      },
    },
  },
  '/contact': {
    title: 'Contact League Runner | Ethan Pelton',
    description:
      'Contact Ethan Pelton and League Runner with feedback, questions, or ideas about fantasy running leagues and athlete competition.',
  },
  '/news': {
    title: 'League Runner News',
    description: 'Read league-event announcements, community stories, and updates from League Runner.',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'League Runner News',
      url: `${SITE_URL}/news`,
      description: 'League-event announcements and community stories from League Runner.',
      publisher: {
        ...ORGANIZATION_SCHEMA,
      },
    },
  },
  '/privacy': {
    title: 'Privacy Policy | League Runner',
    description: 'Read the League Runner privacy policy and how athlete, league, and account data are handled.',
  },
  '/terms': {
    title: 'Terms of Service | League Runner',
    description: 'Review the League Runner terms of service for leagues, athlete participation, and site usage.',
  },
  '/demo': {
    title: 'League Runner Demo',
    description: 'Preview how League Runner organizes leagues, teams, athlete tracking, and season competition.',
  },
};

const PRIVATE_DEFAULT_METADATA = {
  title: `${SITE_NAME} App`,
  description: DEFAULT_DESCRIPTION,
  robots: 'noindex,nofollow',
};

const ensureMetaTag = (attribute, key) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  return tag;
};

const ensureCanonicalTag = () => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  return link;
};

const ensureStructuredDataTag = () => {
  let script = document.head.querySelector('script[data-seo="structured-data"]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'structured-data');
    document.head.appendChild(script);
  }
  return script;
};

const normalizePathname = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

const resolveMetadata = (pathname) => {
  const normalizedPath = normalizePathname(pathname);
  if (normalizedPath.startsWith('/news/')) {
    return {
      title: `${SITE_NAME} News`,
      description: 'Read the latest League Runner update.',
      robots: 'index,follow',
      canonicalUrl: `${SITE_URL}${normalizedPath}`,
      imageUrl: DEFAULT_IMAGE_URL,
      structuredData: null,
    };
  }

  const publicMetadata = PUBLIC_ROUTE_METADATA[normalizedPath];

  if (publicMetadata) {
    return {
      title: publicMetadata.title,
      description: publicMetadata.description,
      robots: 'index,follow',
      canonicalUrl: `${SITE_URL}${normalizedPath === '/' ? '/' : normalizedPath}`,
      imageUrl: DEFAULT_IMAGE_URL,
      structuredData: publicMetadata.structuredData || null,
    };
  }

  return {
    ...PRIVATE_DEFAULT_METADATA,
    canonicalUrl: `${SITE_URL}${normalizedPath}`,
    imageUrl: DEFAULT_IMAGE_URL,
    structuredData: null,
  };
};

export const applySeoMetadata = (pathname) => {
  const metadata = resolveMetadata(pathname);
  const canonicalHref = metadata.canonicalUrl || SITE_URL;

  document.title = metadata.title;

  ensureMetaTag('name', 'description').setAttribute('content', metadata.description || DEFAULT_DESCRIPTION);
  ensureMetaTag('name', 'robots').setAttribute('content', metadata.robots || 'index,follow');
  ensureMetaTag('property', 'og:site_name').setAttribute('content', SITE_NAME);
  ensureMetaTag('property', 'og:type').setAttribute('content', 'website');
  ensureMetaTag('property', 'og:title').setAttribute('content', metadata.title);
  ensureMetaTag('property', 'og:description').setAttribute('content', metadata.description || DEFAULT_DESCRIPTION);
  ensureMetaTag('property', 'og:url').setAttribute('content', canonicalHref);
  ensureMetaTag('property', 'og:image').setAttribute('content', metadata.imageUrl || DEFAULT_IMAGE_URL);
  ensureMetaTag('name', 'twitter:card').setAttribute('content', 'summary_large_image');
  ensureMetaTag('name', 'twitter:title').setAttribute('content', metadata.title);
  ensureMetaTag('name', 'twitter:description').setAttribute('content', metadata.description || DEFAULT_DESCRIPTION);
  ensureMetaTag('name', 'twitter:image').setAttribute('content', metadata.imageUrl || DEFAULT_IMAGE_URL);

  ensureCanonicalTag().setAttribute('href', canonicalHref);

  const structuredDataTag = ensureStructuredDataTag();
  if (metadata.structuredData) {
    structuredDataTag.textContent = JSON.stringify(metadata.structuredData);
  } else {
    structuredDataTag.textContent = '';
  }
};
